import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { log, styles } from "@repo/helpers";
import { Button, ButtonSize, ButtonTypes, Col } from "@repo/ui";
import { Children, ComponentElement, useCallback, useEffect, useMemo, useState } from "react";

import { scrollToField, scrollToSubsection } from ".";
import { ButtonContainer, DEButton } from "./buttons";
import { ButtonContainerProps } from "./buttons/types";
import { ComponentProvider } from "./contexts/componentContext";
import { useDynamicSectionContext } from "./contexts/dynamicSectionContext";
import { useEditDataContext } from "./contexts/editDataContext";
import DynamicComponentContainer from "./dynamicComponentContainer";
import DynamicComponentList from "./dynamicComponentList";
import style from "./style.module.scss";
import { Props, State, WebViewProps } from "./types";

export function WebViewContainer(props: WebViewProps) {
	const {
		showList,
		showButtonNav,
		containerClass,
		innerContainerClass,
		children,
		valueChanged,
		valuesChanged,
		customFunctions,
		setCopiedFrom,
		deleteUploadedFile,
		getUploadedFile,
		getCustomComponent,
		getComponentAddon,
		getComponentAddonPosition,
		locationTracker,
		...rest
	} = props;
	const { readOnly } = rest;
	const { visibleSections, sectionMap } = useDynamicSectionContext();
	const [state, setState] = useState(
		() =>
			({
				activeSection: null,
				activeSectionName: null,
				manual: false,
				jumpedField: null,
			}) as State,
	);
	const updateState = (newState: Partial<State>) => setState({ ...state, ...newState });
	const { activeSection, activeSectionName, manual, jumpedField } = state;

	const editDataContext = useEditDataContext();

	useEffect(() => {
		if (manual) {
			scrollIntoView();
		}
	}, [activeSectionName, manual, jumpedField]);

	const scrollIntoView = useCallback(
		function () {
			if (state.jumpedField && scrollToSubsection(jumpedField)) return;

			const el =
				document.querySelector(`div[data-entrySection='${activeSectionName}']`) ||
				document.getElementById(activeSectionName);
			if (el) {
				el.scrollIntoView();
			} else {
				log(`No element matching section ${activeSectionName} found.  Could not scroll to`);
			}
		},
		[state.jumpedField, activeSectionName],
	);

	const scrollToSection = useCallback(
		async function scrollToSection(section: string, field?: string) {
			const selectedSection = sectionMap.get(section);
			let index = visibleSections.indexOf(selectedSection);
			if (index === -1) index = 0;

			updateState({
				activeSection: index,
				activeSectionName: section,
				manual: true,
				jumpedField: field,
			});
		},
		[visibleSections],
	);

	const scrollNext = useCallback(
		function () {
			let section = activeSection ?? 0;
			if (section >= visibleSections.length - 1) return;
			section++;
			while (section < visibleSections.length - 1) {
				section++;
			}
			scrollToSection(visibleSections[section].linkName);
		},
		[activeSection, visibleSections, scrollToSection],
	);

	const scrollPrevious = useCallback(
		function () {
			let section = activeSection ?? 0;
			if (!section) return;
			section--;
			while (section > 0 && visibleSections[section]) {
				section--;
			}
			scrollToSection(visibleSections[section].linkName);
		},
		[activeSection, visibleSections, scrollToSection],
	);

	const updateActiveNode = useCallback(
		function () {
			const activeSection = getActiveSectionId();

			const activeSectionName = visibleSections[activeSection]?.linkName;
			updateState({
				activeSection,
				activeSectionName,
				manual: false,
				jumpedField: null,
			});
		},
		[visibleSections, updateState],
	);
	const onScroll = useCallback(
		function () {
			if (manual) updateState({ manual: false });
			else updateActiveNode();
		},
		[updateState, manual, updateActiveNode],
	);

	const scrollToFieldWrap = useCallback(() => scrollToField, []);
	const summaryEditPageExists = useCallback(() => true, []);

	const buttonContainer = useMemo(
		() =>
			Children.map(children, (c: ComponentElement<ButtonContainerProps, any>) => {
				if (c?.type?.name === ButtonContainer.name)
					return {
						...c,
						props: { ...c.props, hideAll: c.props.hideAll ?? readOnly },
					};
				return null;
			}).filter((a) => a),
		[children, readOnly],
	);

	return (
		<ComponentProvider
			valueChanged={editDataContext?.updateProperty ?? valueChanged}
			valuesChanged={editDataContext?.updateProperties ?? valuesChanged}
			setCopiedFrom={editDataContext?.toggleCopiedProperties ?? setCopiedFrom}
			deleteUploadedFile={deleteUploadedFile}
			getUploadedFile={getUploadedFile}
			getCustomComponent={getCustomComponent}
			getComponentAddon={getComponentAddon}
			getComponentAddonPosition={getComponentAddonPosition}
			customFunctions={customFunctions}
			locationTracker={locationTracker}
		>
			{renderList()}
			<Col
				md={showList ? 10 : 12}
				className={styles(style.outerContainer, containerClass)}
				id={"dataEntryContainer"}
				onScroll={onScroll}
			>
				<div className={innerContainerClass}>
					<DynamicComponentContainer
						{...rest}
						{...state}
						scrollToField={scrollToFieldWrap}
						summaryEditPageExists={summaryEditPageExists}
					/>
					{buttonContainer}
				</div>
			</Col>
			{renderButtonNavigation()}
		</ComponentProvider>
	);

	function renderButtonNavigation() {
		if (!showButtonNav) return null;
		return (
			<section className={style.webViewNavButtonWrapper} id={"buttonNavigation"}>
				<div className={style.webViewNavButtonContainer}>
					<Button onClick={scrollPrevious} size={ButtonSize.large}>
						<span>
							<FontAwesomeIcon icon={faArrowUp} />
						</span>
					</Button>
					<Button types={[ButtonTypes.Primary]} onClick={scrollNext} size={ButtonSize.large}>
						<span>
							<FontAwesomeIcon icon={faArrowDown} />
						</span>
					</Button>
				</div>
			</section>
		);
	}

	function renderList(width = 2) {
		if (!showList) return null;
		return (
			<Col md={width} id="navigation" className={styles(style.scriptNav, style.middleContainers)} key="buttons">
				<Col md={12}>
					<DynamicComponentList
						{...rest}
						{...state}
						activeSection={state.activeSectionName}
						scrollToSection={scrollToSection}
						scrollToSubsection={scrollToSubsection}
					/>
				</Col>
			</Col>
		);
	}
}

/**This is here for legacy purposes, using the old button controls */
export default function DynamicEditor(props: Props) {
	const { readOnly, completed, submitText, submit, cancel } = props;
	return (
		<WebViewContainer {...props}>
			<ButtonContainer>{renderButtons()}</ButtonContainer>
		</WebViewContainer>
	);

	function renderButtons() {
		return (
			<ButtonContainer completed={completed} hideAll={readOnly}>
				<DEButton onClick={submit} types={[ButtonTypes.Primary]} mustBeComplete>
					{(typeof submitText == "string" ? submitText : submitText()) || "Save"}
				</DEButton>
				{renderCancelBtn()}
			</ButtonContainer>
		);
	}

	function renderCancelBtn() {
		if (!cancel) return null;
		return <DEButton onClick={cancel}>Cancel</DEButton>;
	}
}

function getActiveSectionId() {
	const dataEntryContainer = document.getElementById("dataEntryContainer");
	if (dataEntryContainer.scrollTop === 0) return 0;

	const sections = document.querySelectorAll("div[data-entrySection]");
	if (dataEntryContainer.scrollTop + dataEntryContainer.offsetHeight + 1 >= dataEntryContainer.scrollHeight)
		return sections.length - 1;

	const boundingBox = dataEntryContainer.getBoundingClientRect();
	for (let i = 0; i < sections.length; i++) {
		const section = sections[i];
		if (section.getBoundingClientRect().top + section.getBoundingClientRect().height - boundingBox.top > 0)
			return i;
	}
}
